/**
 * File: error_dialog.tsx
 * Description: Reusable error dialog component for the admin interface. Displays an
 * error title and message returned from an API call, with a single close button to
 * dismiss it.
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme
} from '@mui/material';
import { ColorsPalletTheme } from "@/constants/colors_pallet_theme";

/**
 * Props for the ErrorDialog component.
 *
 * @property {boolean} open - Whether the dialog is visible.
 * @property {string} title - Text displayed in the dialog title bar.
 * @property {string} message - Error message shown in the dialog body.
 * @property {() => void} onClose - Called when the user closes the dialog.
 */
interface ErrorDialogProps {
  open: boolean;
  title: string;
  message: string;
  onClose: () => void;
}

/**
 * Pure presentational dialog for surfacing API or runtime errors to the admin user.
 * Shows a title, a descriptive error message, and a single "Cerrar" button.
 *
 * @param {boolean} props.open - Controls dialog visibility.
 * @param {string} props.title - Dialog title text.
 * @param {string} props.message - Error message to display in the body.
 * @param {() => void} props.onClose - Handler invoked when the close button is clicked.
 * @returns {JSX.Element}
 */
function ErrorDialog({ open, title, message, onClose }: ErrorDialogProps) {

  // Main App Theme
  const theme = useTheme();
  const colors = ColorsPalletTheme(theme.palette.mode);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography>Error: {message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose}
          sx={{
            backgroundColor: colors.blueAccent[400], 
            color: 'white',
            "&:hover": {
                background: colors.blueAccent[300],
            },
          }}
        >
            Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ErrorDialog;
